import { useEffect, useMemo, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { useDropdown } from '../hooks/useDropdown';
import {
	StyledArrow,
	StyledContainer,
	StyledDropdown,
	StyledDropdownItem,
	StyledDropdownList,
	StyledInputWrapper,
	StyledSearchInput,
	StyledSearchWrapper,
} from './MultiSelect.styled';

interface MultiSelectOption {
	value: string;
	label: string;
	category?: string;
}

interface MultiSelectProps {
	options: MultiSelectOption[];
	values: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	filterPlaceholder?: string;
	singleSelectCategory?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
	options,
	values,
	onChange,
	placeholder = 'Выберите...',
	filterPlaceholder = 'Поиск...',
	singleSelectCategory,
}) => {
	const [debouncedFilter, setDebouncedFilter] = useState('');

	const {
		isOpen,
		filterText,
		filteredOptions: rawFilteredOptions,
		containerRef,
		inputRef,
		listRef,
		handleToggle,
		handleInputChange,
		handleInputKeyDown,
	} = useDropdown(options, '', () => {});

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setDebouncedFilter(filterText);
		}, 200);
		return () => clearTimeout(timeoutId);
	}, [filterText]);

	const filteredOptions = useMemo(() => {
		if (!debouncedFilter.trim()) {
			return rawFilteredOptions;
		}
		const lowerFilter = debouncedFilter.toLowerCase();
		return rawFilteredOptions.filter(
			(opt) =>
				opt.value.toLowerCase().includes(lowerFilter) ||
				opt.label.toLowerCase().includes(lowerFilter),
		);
	}, [rawFilteredOptions, debouncedFilter]);

	const selectedLabels = useMemo(() => {
		return values
			.map((v) => {
				const opt = options.find((o) => o.value === v);
				return opt?.label || v;
			})
			.join(', ');
	}, [values, options]);

	const handleSelect = (option: MultiSelectOption) => {
		playSound(Sound.LinkCursor);

		if (values.includes(option.value)) {
			onChange(values.filter((v) => v !== option.value));
		} else {
			if (singleSelectCategory && option.category === singleSelectCategory) {
				const otherValues = values.filter((v) => {
					const opt = options.find((o) => o.value === v);
					return opt?.category !== singleSelectCategory;
				});
				onChange([...otherValues, option.value]);
			} else {
				onChange([...values, option.value]);
			}
		}
		setDebouncedFilter('');
	};

	const isOptionSelected = (option: MultiSelectOption) => {
		return values.includes(option.value);
	};

	return (
		<StyledContainer ref={containerRef}>
			<StyledInputWrapper $focus={isOpen} onClick={handleToggle}>
				<Text
					style={{
						flex: 1,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{selectedLabels || placeholder}
				</Text>
				<StyledArrow $active={isOpen}>▼</StyledArrow>
			</StyledInputWrapper>
			{isOpen && (
				<StyledDropdown>
					<StyledDropdownList ref={listRef}>
						{filteredOptions.length === 0 ? (
							<StyledDropdownItem disabled>
								<Text style={{ opacity: 0.5 }}>Ничего не найдено</Text>
							</StyledDropdownItem>
						) : (
							filteredOptions.map((option) => (
								<StyledDropdownItem
									key={option.value}
									$selected={isOptionSelected(option)}
									onClick={(e) => {
										e.stopPropagation();
										handleSelect(option);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<Text>{option.label}</Text>
									{option.value && (
										<Text style={{ opacity: 0.5, fontSize: 12, marginLeft: 8 }}>
											({option.value})
										</Text>
									)}
								</StyledDropdownItem>
							))
						)}
					</StyledDropdownList>
					<StyledSearchWrapper>
						<StyledSearchInput
							ref={inputRef}
							value={filterText}
							onChange={handleInputChange}
							onKeyDown={handleInputKeyDown}
							placeholder={filterPlaceholder}
							autoFocus
						/>
					</StyledSearchWrapper>
				</StyledDropdown>
			)}
		</StyledContainer>
	);
};
